import { Calendar, Compass, MessageCircle, TrendingUp, Users } from 'react-feather';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Permission } from '../../__generated__/graphql';
import { assetUrl } from '../../common/helpers';
import Logo from './Logo';
import ProtectedSection from './ProtectedSection';
import UI from '../../common/UI';
import UserNav from './UserNav';
import useMediaDevice from '../../common/useMediaDevice';
import useProject from '../useProject';

const NavBar = () => {
  const { t } = useTranslation();
  const project = useProject();
  const mediaDevice = useMediaDevice();

  if (mediaDevice.isSmall && !project) {
    return <></>;
  }

  return (
    <NavBarContainer>
      <Inner>
        {!mediaDevice.isSmall && (
          <UI.Div>
            <Logo to={project ? `/${project.id}` : '/'}>
              <img src={assetUrl('images/logo-color-light.svg')} alt={t('atleta')} />
            </Logo>
          </UI.Div>
        )}

        {project && (
          <MainNav>
            <ProtectedSection permissions={[Permission.event_dashboards]}>
              <MainNavItem to={`/${project.id}/dashboard`}>
                <UI.Icon>
                  <Compass />
                </UI.Icon>
                {' '}
                {t('nav.dashboard')}
              </MainNavItem>
            </ProtectedSection>
            <ProtectedSection permissions={[Permission.edit_events]}>
              <MainNavItem to={`/${project.id}/events`}>
                <UI.Icon>
                  <Calendar />
                </UI.Icon>
                {' '}
                {t('nav.events')}
              </MainNavItem>
            </ProtectedSection>
            <ProtectedSection permissions={[Permission.view_registrations]}>
              <MainNavItem
                isActive={(_, { pathname }) => (
                  [`/${project.id}/participants`, `/${project.id}/teams`].some(
                    (rootPath) => pathname.includes(rootPath),
                  )
                )}
                to={`/${project.id}/participants`}
              >
                <UI.Icon>
                  <Users />
                </UI.Icon>
                {' '}
                {t('nav.participants')}
              </MainNavItem>
            </ProtectedSection>
            <ProtectedSection permissions={[Permission.messages]}>
              <MainNavItem to={`/${project.id}/communication`}>
                <UI.Icon>
                  <MessageCircle />
                </UI.Icon>
                {' '}
                {t('nav.communication')}
              </MainNavItem>
            </ProtectedSection>
            <ProtectedSection permissions={[Permission.financial_reports]}>
              <MainNavItem to={`/${project.id}/reports`}>
                <UI.Icon>
                  <TrendingUp />
                </UI.Icon>
                {' '}
                {t('nav.reports')}
              </MainNavItem>
            </ProtectedSection>
          </MainNav>
        )}

        {!project && <UI.Div />}

        <UI.Div sc={{ color: 'gray[200]' }}>
          {!mediaDevice.isSmall && <UserNav />}
        </UI.Div>
      </Inner>
    </NavBarContainer>
  );
};

const NavBarContainer = styled.div`
  ${({ theme }) => css`
    position: fixed;
    background: ${theme.transparentize(theme.colors.secondary[600], 0.1)};
    backdrop-filter: blur(5px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    z-index: 500;
    left: 0;
    right: 0;

    @media ${theme.devices.sm} {
      bottom: 0;
      height: ${theme.navBarHeight.sm}px;
    }

    @media ${theme.devices.md} {
      min-height: auto;
      bottom: initial;
      height: ${theme.navBarHeight.md}px;
      top: 0;
    }
  `}
`;

const Inner = styled(UI.Div)`
  ${({ theme }) => css`
    height: 100%;

    @media ${theme.devices.sm} {
      padding: 0;
      display: flex;
      justify-content: space-between;
    }

    @media ${theme.devices.md} {
      padding: 0 ${theme.gutter}px;
      display: grid;
      grid-template-columns: 2fr 5fr 2fr;
    }
  `}
`;

const MainNav = styled.ul`
  ${({ theme }) => css`
    white-space: nowrap;

    @media ${theme.devices.sm} {
      display: flex;
      justify-content: space-evenly;
      width: 100%;
      padding: 3px;
    }

    @media ${theme.devices.md} {
      display: inline-block;
      width: auto;
      text-align: center;
      padding: 0;
    }
  `}
`;

const MainNavItem = styled(NavLink)`
  ${({ theme }) => css`
    position: relative;
    color: ${theme.colors.secondary[100]};
    font-weight: 500;
    display: inline-block;
    transition: all 0.15s ease-in-out;
    font-family: ${theme.fontFamilies.headings};
    border-radius: ${theme.borderRadiuses.md}px;

    &:hover {
      background: ${theme.transparentize(theme.colors.secondary[400], 0.3)};
    }

    &.active {
      background: ${theme.transparentize(theme.colors.secondary[400], 0.3)};
      color: ${theme.colors.white};
    }

    @media ${theme.devices.sm} {
      display: flex;
      text-align: center;
      justify-content: center;
      align-content: center;
      flex-wrap: wrap;
      width: 33.33%;
      font-size: calc(0.6em + 0.5vw);
      margin: 3px;
    }

    @media ${theme.devices.md} {
      display: inline-block;
      padding: 0 ${theme.gutter / 3}px;
      height: ${theme.navBarHeight.md - 24}px;
      line-height: ${theme.navBarHeight.md - 24}px;
      width: auto;
      font-size: ${theme.fontSizes.body}rem;
      margin: 12px 2px;
    }

    @media ${theme.devices.sm} {
      flex-direction: column;
      align-items: center;

      ${UI.Icon} svg {
        stroke-width: 1.5;
        width: 1.5em;
        height: 1.5em;
      }
    }

    @media ${theme.devices.md} {
      ${UI.Icon} {
        width: auto;
        margin-right: .2em;

        svg {
          width: 1.333em;
          height: 1.333em;
        }
      }
    }
  `}
`;

export default NavBar;
